// 博客管理器 - 自动扫描和解析 markdown 文件

// 使用 webpack 的 require.context 来动态导入所有 markdown 文件
function importAll(r) {
  let posts = []
  r.keys().forEach(key => {
    const content = r(key).default || r(key)
    const pathParts = key.split('/')
    const fileName = pathParts[pathParts.length - 1].replace('.md', '')
    const category = pathParts[pathParts.length - 2] || 'General'
    
    // 解析 frontmatter 和内容
    const parsed = parseFrontmatter(content)
    
    if (parsed.frontmatter && parsed.content) {
      posts.push({
        id: fileName,
        category: parsed.frontmatter.category || category,
        ...parsed.frontmatter,
        content: parsed.content,
        path: key
      })
    }
  })
  return posts
}

// 解析 frontmatter (YAML 格式的元数据)
function parseFrontmatter(content) {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/
  const match = content.match(frontmatterRegex)
  
  if (!match) {
    return { frontmatter: null, content: content }
  }
  
  const frontmatterText = match[1]
  const markdownContent = match[2]
  
  try {
    const frontmatter = parseYAML(frontmatterText)
    return {
      frontmatter,
      content: markdownContent
    }
  } catch (error) {
    console.error('Error parsing frontmatter:', error)
    return { frontmatter: null, content: content }
  }
}

// 简单的 YAML 解析器（支持基本的键值对和数组）
function parseYAML(yamlText) {
  const lines = yamlText.split('\n')
  const result = {}
  let currentKey = null
  let currentValue = null
  let isArray = false
  let isObject = false
  let objectKey = null
  
  for (let line of lines) {
    line = line.trim()
    if (!line || line.startsWith('#')) continue
    
    // 处理数组项
    if (line.startsWith('- ')) {
      if (isArray && currentKey) {
        if (!Array.isArray(result[currentKey])) {
          result[currentKey] = []
        }
        const value = line.substring(2).trim()
        result[currentKey].push(parseValue(value))
      }
      continue
    }
    
    // 处理键值对
    const colonIndex = line.indexOf(':')
    if (colonIndex > 0) {
      const key = line.substring(0, colonIndex).trim()
      const value = line.substring(colonIndex + 1).trim()
      
      // 检查是否是对象的开始
      if (!value) {
        currentKey = key
        isObject = true
        isArray = false
        result[key] = {}
        continue
      }
      
      // 检查是否是数组的开始
      if (value === '[]' || (value.startsWith('[') && value.endsWith(']'))) {
        currentKey = key
        isArray = true
        isObject = false
        if (value === '[]') {
          result[key] = []
        } else {
          // 解析内联数组
          const arrayContent = value.slice(1, -1)
          result[key] = arrayContent.split(',').map(item => parseValue(item.trim()))
        }
        continue
      }
      
      // 普通键值对
      if (isObject && currentKey) {
        result[currentKey][key] = parseValue(value)
      } else {
        result[key] = parseValue(value)
        isArray = false
        isObject = false
        currentKey = null
      }
    }
  }
  
  return result
}

// 解析值的类型
function parseValue(value) {
  if (!value) return ''
  
  // 移除引号
  if ((value.startsWith('"') && value.endsWith('"')) || 
      (value.startsWith("'") && value.endsWith("'"))) {
    return value.slice(1, -1)
  }
  
  // 布尔值
  if (value === 'true') return true
  if (value === 'false') return false
  
  // 数字
  if (!isNaN(value) && !isNaN(parseFloat(value))) {
    return parseFloat(value)
  }
  
  return value
}

// 动态导入所有博客文章
let allPosts = []

try {
  // 使用 webpack 的 require.context 来动态导入
  const blogContext = require.context('@/blog/posts', true, /\.md$/)
  allPosts = importAll(blogContext)
} catch (error) {
  console.warn('No blog posts found or error loading posts:', error)
  allPosts = []
}

// 博客管理类
class BlogManager {
  constructor() {
    this.posts = allPosts
  }
  
  // 获取所有文章
  getAllPosts() {
    return this.posts.sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt))
  }
  
  // 根据ID获取文章
  getPostById(id) {
    return this.posts.find(post => post.id === id)
  }
  
  // 获取特色文章
  getFeaturedPosts() {
    return this.posts.filter(post => post.featured)
  }
  
  // 根据分类获取文章
  getPostsByCategory(category) {
    return this.posts.filter(post => post.category === category)
  }
  
  // 根据标签获取文章
  getPostsByTag(tag) {
    return this.posts.filter(post => post.tags && post.tags.includes(tag))
  }
  
  // 搜索文章
  searchPosts(query, language = 'en') {
    const searchTerm = query.toLowerCase()
    return this.posts.filter(post => {
      const title = this.getLocalizedContent(post.title, language).toLowerCase()
      const excerpt = this.getLocalizedContent(post.excerpt, language).toLowerCase()
      const tags = (post.tags || []).join(' ').toLowerCase()
      const category = post.category.toLowerCase()
      
      return title.includes(searchTerm) || 
             excerpt.includes(searchTerm) || 
             tags.includes(searchTerm) ||
             category.includes(searchTerm)
    })
  }
  
  // 获取所有分类
  getAllCategories() {
    const categories = new Set()
    this.posts.forEach(post => {
      if (post.category) {
        categories.add(post.category)
      }
    })
    return Array.from(categories).sort()
  }
  
  // 获取所有标签
  getAllTags() {
    const tags = new Set()
    this.posts.forEach(post => {
      if (post.tags && Array.isArray(post.tags)) {
        post.tags.forEach(tag => tags.add(tag))
      }
    })
    return Array.from(tags).sort()
  }
  
  // 获取本地化内容
  getLocalizedContent(content, language = 'en') {
    if (typeof content === 'string') return content
    if (typeof content === 'object' && content !== null) {
      return content[language] || content.en || content.zh || ''
    }
    return ''
  }
  
  // 获取相关文章
  getRelatedPosts(postId, limit = 3) {
    const currentPost = this.getPostById(postId)
    if (!currentPost) return []
    
    const related = this.posts
      .filter(post => post.id !== postId)
      .map(post => {
        let score = 0
        
        // 相同分类加分
        if (post.category === currentPost.category) {
          score += 3
        }
        
        // 相同标签加分
        if (currentPost.tags && post.tags) {
          const commonTags = currentPost.tags.filter(tag => post.tags.includes(tag))
          score += commonTags.length * 2
        }
        
        return { post, score }
      })
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
      .map(item => item.post)
    
    return related
  }
  
  // 添加新文章（用于动态添加）
  addPost(postData) {
    // 生成ID
    const id = postData.id || this.generateId(postData.title)
    
    const newPost = {
      id,
      publishedAt: new Date().toISOString().split('T')[0],
      ...postData
    }
    
    this.posts.push(newPost)
    return newPost
  }
  
  // 生成文章ID
  generateId(title) {
    const titleText = typeof title === 'string' ? title : (title.en || title.zh || 'untitled')
    return titleText
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
  }
  
  // 获取文章统计信息
  getStats() {
    return {
      totalPosts: this.posts.length,
      categories: this.getAllCategories().length,
      tags: this.getAllTags().length,
      featuredPosts: this.getFeaturedPosts().length
    }
  }
}

// 创建单例实例
const blogManager = new BlogManager()

export default blogManager

// 导出便捷方法
export const {
  getAllPosts,
  getPostById,
  getFeaturedPosts,
  getPostsByCategory,
  getPostsByTag,
  searchPosts,
  getAllCategories,
  getAllTags,
  getRelatedPosts
} = blogManager